import { type ReactElement } from "react";

import {
  ThemedButton,
  type ThemedButtonProps,
} from "@vega-core/components/Buttons/ThemedButton";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { isTruthy } from "@vega-core/utils/isTruthy";

export type ModalButtonsGroupProps = {
  className?: string;
  disabled?: boolean;
  disabledSecondary?: boolean;
  isLoading?: boolean;
  mainAction?: VoidFunction;
  mainActionLabel?: string;
  mainActionText?: ReactElement | string;
  mainActionVariant?: "destructive" | "primary";
  fullWidthMainAction?: boolean;
  secondaryAction?: VoidFunction;
  secondaryActionLabel?: string;
  secondaryActionText?: ReactElement | string;
  secondaryActionIcon?: ReactElement | string;
  secondaryActionVariant?: ThemedButtonProps["variant"];
  thirdActionElement?: ReactElement | string;
  fullWidthSecondaryAction?: boolean;
};

export function ModalButtonsGroup({
  className,
  disabled,
  disabledSecondary,
  isLoading,
  mainAction,
  mainActionLabel,
  mainActionText,
  mainActionVariant = "primary",
  secondaryAction,
  secondaryActionIcon,
  secondaryActionLabel,
  secondaryActionText,
  secondaryActionVariant = "secondary",
  thirdActionElement,
  fullWidthMainAction = false,
  fullWidthSecondaryAction = false,
}: ModalButtonsGroupProps) {
  return (
    <div
      className={cnTwMerge(
        "flex flex-col-reverse sm:flex-row items-center justify-end w-full mt-4 gap-4 sm:gap-0 sm:space-x-4",
        className,
      )}
    >
      {isTruthy(thirdActionElement) ? (
        <div className="mr-auto">{thirdActionElement}</div>
      ) : null}
      {secondaryAction ? (
        <ThemedButton
          ariaLabel={secondaryActionLabel}
          className="w-full sm:w-fit"
          dataTestId="secondary-action-button"
          disabled={disabledSecondary}
          fullWidth={fullWidthSecondaryAction}
          icon={secondaryActionIcon}
          onClick={secondaryAction}
          variant={secondaryActionVariant}
        >
          {isTruthy(secondaryActionText) ? secondaryActionText : m.btn_cancel()}
        </ThemedButton>
      ) : null}
      {mainAction ? (
        <ThemedButton
          ariaLabel={mainActionLabel}
          className="w-full sm:w-fit"
          dataTestId="main-action-button"
          disabled={disabled}
          fullWidth={fullWidthMainAction}
          isLoading={isLoading}
          onClick={mainAction}
          variant={mainActionVariant}
        >
          {isTruthy(mainActionText) ? mainActionText : m.btn_submit()}
        </ThemedButton>
      ) : null}
    </div>
  );
}
